import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  LineChart, Line, ResponsiveContainer, Area, AreaChart, PieChart, Pie, Cell, Tooltip, XAxis, YAxis, CartesianGrid
} from "recharts";
import {
  DollarSign, ShoppingCart, Users, Tag, TrendingUp, Receipt, Plus, Image as ImageIcon, Ticket, BarChart3, Eye, FileText, AlertCircle
} from "lucide-react";
import { api, formatPrice } from "../../lib/api";
import { useI18n } from "../../i18n";

const COLORS = ["#A855F7", "#C084FC", "#7E22CE", "#9333EA", "#D8B4FE"];
const STATUS_COLORS = { completed: "#10B981", pending: "#F59E0B", cancelled: "#EF4444", refunded: "#A855F7" };

export default function AdminDashboard() {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)); }, []);

  if (!data) {
    return <div className="grid grid-cols-1 md:grid-cols-5 gap-4">{Array.from({length:5}).map((_,i)=> <div key={i} className="h-28 rounded-2xl shimmer opacity-30" />)}</div>;
  }

  const { kpis, sales_series, by_category, top_products, by_status, recent_orders } = data;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-display text-3xl font-bold text-white">{t("admin.dashboard")}</h1>
        <p className="text-slate-400 mt-1">{t("admin.welcome")}</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi title={t("admin.total_sales")} value={formatPrice(kpis.total_revenue)} icon={DollarSign} delta="+12.8%" />
        <Kpi title={t("admin.orders")} value={kpis.total_orders.toString()} icon={ShoppingCart} delta="+8.1%" />
        <Kpi title={t("admin.users")} value={kpis.total_users.toString()} icon={Users} delta="+11.3%" />
        <Kpi title={t("admin.avg_ticket")} value={formatPrice(kpis.avg_ticket)} icon={Tag} delta="+4.3%" />
        <Kpi title={t("admin.conversion")} value={`${kpis.conversion_rate}%`} icon={TrendingUp} delta="+1.2%" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Sales line */}
        <Panel title={t("admin.sales_30")} className="lg:col-span-2">
          <div className="h-[260px]">
            <ResponsiveContainer>
              <AreaChart data={sales_series}>
                <defs>
                  <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#A855F7" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#A855F7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#171723", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} labelStyle={{ color: "#fff" }} formatter={(v) => formatPrice(v)} />
                <Area type="monotone" dataKey="value" stroke="#A855F7" strokeWidth={2} fill="url(#salesGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        {/* By category donut */}
        <Panel title={t("admin.by_category")}>
          <div className="h-[260px] flex items-center justify-center">
            {by_category.length === 0 ? (
              <div className="text-slate-500 text-sm">No data yet</div>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={by_category} dataKey="value" nameKey="category" innerRadius={50} outerRadius={80} paddingAngle={3}>
                    {by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#171723", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} formatter={(v) => formatPrice(v)} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="space-y-1.5 mt-2">
            {by_category.map((c, i) => (
              <div key={c.category} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-slate-300"><span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{c.category}</span>
                <span className="font-semibold text-white">{formatPrice(c.value)}</span>
              </div>
            ))}
          </div>
        </Panel>

        {/* Status donut */}
        <Panel title={t("admin.orders_status")}>
          <div className="h-[260px] flex items-center justify-center">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={by_status} dataKey="count" nameKey="status" innerRadius={50} outerRadius={80} paddingAngle={3}>
                  {by_status.map((s) => <Cell key={s.status} fill={STATUS_COLORS[s.status] || "#A855F7"} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#171723", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 mt-2">
            {by_status.map((s) => (
              <div key={s.status} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-slate-300 capitalize"><span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[s.status] || "#A855F7" }} />{s.status}</span>
                <span className="font-semibold text-white">{s.count}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Top products + recent orders */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title={t("admin.top_products")} className="lg:col-span-1">
          <div className="space-y-3">
            {top_products.length === 0 && <div className="text-slate-500 text-sm">No sales yet</div>}
            {top_products.map((p) => (
              <div key={p.product_id} className="flex items-center gap-3">
                <img src={p.cover_image} alt={p.title} className="w-10 h-10 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{p.title}</div>
                  <div className="text-xs text-slate-500">{p.sales} vendas</div>
                </div>
                <div className="text-xs font-bold text-[#A855F7]">{formatPrice(p.revenue)}</div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title={t("admin.recent_orders")} className="lg:col-span-2">
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">ID</th>
                  <th className="px-2 py-2 text-left font-medium">Cliente</th>
                  <th className="px-2 py-2 text-left font-medium">Produtos</th>
                  <th className="px-2 py-2 text-left font-medium">Valor</th>
                  <th className="px-2 py-2 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {recent_orders.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-slate-500">No orders yet</td></tr>}
                {recent_orders.map((o) => (
                  <tr key={o.id} className="border-t border-white/5">
                    <td className="px-2 py-3 font-medium text-white">#{o.id.slice(0, 6).toUpperCase()}</td>
                    <td className="px-2 py-3 text-slate-300">{o.user_email}</td>
                    <td className="px-2 py-3 text-slate-300 truncate max-w-[140px]">{o.items?.[0]?.title}</td>
                    <td className="px-2 py-3 font-semibold text-white">{formatPrice(o.total)}</td>
                    <td className="px-2 py-3"><StatusBadge status={o.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <QuickAction icon={Plus} label="Adicionar Produto" to="/admin/products" />
        <QuickAction icon={ImageIcon} label="Adicionar Banner" to="/admin/banners" />
        <QuickAction icon={Ticket} label="Gerenciar Cupons" to="/admin/coupons" />
        <QuickAction icon={BarChart3} label="Ver Relatórios" to="/admin/reports" />
        <QuickAction icon={Eye} label="Ver Pedidos" to="/admin/orders" />
        <QuickAction icon={Users} label="Ver Usuários" to="/admin/users" />
      </div>
    </div>
  );
}

function Kpi({ title, value, icon: Icon, delta }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-[#171723] border border-white/5 p-5" data-testid="kpi-card">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{title}</div>
          <div className="font-display text-2xl font-bold text-white mt-2">{value}</div>
          {delta && <div className="text-xs text-emerald-400 mt-1">{delta} em relação ao mês anterior</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-[#A855F7]/15 grid place-items-center text-[#A855F7]">
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </motion.div>
  );
}

function Panel({ title, children, className = "" }) {
  return (
    <div className={`rounded-2xl bg-[#171723] border border-white/5 p-5 ${className}`}>
      <h3 className="font-display font-semibold text-white mb-4">{title}</h3>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    completed: "bg-emerald-500/15 text-emerald-400",
    pending: "bg-amber-500/15 text-amber-400",
    cancelled: "bg-red-500/15 text-red-400",
    refunded: "bg-purple-500/15 text-purple-400",
  };
  return <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold capitalize ${map[status] || "bg-slate-500/15 text-slate-300"}`}>{status}</span>;
}

function QuickAction({ icon: Icon, label, to }) {
  return (
    <Link to={to} className="rounded-2xl bg-[#171723] border border-white/5 p-5 hover:bg-[#1F1F2E] hover:border-[#A855F7]/30 transition-all flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-[#A855F7]/15 grid place-items-center text-[#A855F7]">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-sm font-semibold text-white">{label}</div>
      </div>
    </Link>
  );
}
